import type { Theme, ThemeOptions } from '@mui/material/styles';

/**
 * MUI `Typography` overrides for design-system variants (`h1`–`h6`, `body1` for `body`).
 * The heading scale is strictly decreasing (h1 largest → h6 smallest). These six sizes
 * are a relabeling of the six sizes the app already used pre-fix (36 / 24 / 24 / 20.8 / 20
 * / 12.8, previously out of order across h1–h6) — every existing `variant="hN"` usage was
 * renamed in lockstep so each element keeps rendering at the same pixel size it always
 * did; only which variant name maps to which size changed. Other variants (`body2`,
 * `subtitle1`, `subtitle2`, `caption`, `overline`) intentionally have no override here and
 * render with the base theme's stock MUI sizing (`apps/frontend/src/theme/theme.tsx`
 * doesn't customize them).
 *
 * `navLabel` / `mobileNavLabel` (each scoped via their own `SmTypography-variant-*`
 * class, base `caption`) are dedicated compact styles for nav/menu item labels (the
 * desktop sidebar and the mobile bottom nav, respectively) — kept separate from
 * `caption`/`h4` so those variants' many other usages aren't affected.
 *
 * `bodyLight` is the same size/base as `body` (body1) but at font-weight 300 — for
 * secondary/lighter body copy (e.g. a product's short description or rich description).
 * Same pattern as navLabel/mobileNavLabel: its own `SmTypography-variant-*` class, so the
 * hundreds of existing `body` usages keep their weight-400 look untouched.
 */
export function typographyThemeComponents(_theme: Theme): ThemeOptions['components'] {
  return {
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.SmTypography-root': {
            textTransform: 'none'
          },
          '&.SmTypography-variant-navLabel': {
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: 'normal'
          },
          '&.SmTypography-variant-mobileNavLabel': {
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: 'normal'
          },
          '&.SmTypography-variant-bodyLight': {
            fontWeight: 300
          }
        },
        h1: {
          fontWeight: 400,
          fontSize: '36px'
        },
        h2: {
          fontWeight: 400,
          fontSize: '24px'
        },
        h3: {
          fontWeight: 700,
          fontSize: '24px'
        },
        h4: {
          fontWeight: 700,
          fontSize: '20.8px'
        },
        h5: {
          fontWeight: 700,
          fontSize: '20px'
        },
        h6: {
          fontWeight: 600,
          fontSize: '12.8px'
        },
        body1: {
          fontWeight: 400,
          fontSize: '14px'
        }
      }
    }
  };
}
